import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
	theme: Theme;
	resolved: "light" | "dark";
	setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = "shorty-theme";

function resolveTheme(theme: Theme): "light" | "dark" {
	if (theme === "light" || theme === "dark") return theme;
	if (typeof window === "undefined") return "light";
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [theme, setThemeState] = useState<Theme>(() => {
		try {
			const v = localStorage.getItem(STORAGE_KEY);
			if (v === "light" || v === "dark" || v === "system") return v;
		} catch {
			/* ignore */
		}
		return "system";
	});
	const [resolved, setResolved] = useState<"light" | "dark">(() =>
		resolveTheme(theme),
	);

	useEffect(() => {
		const r = resolveTheme(theme);
		setResolved(r);
		document.documentElement.classList.toggle("dark", r === "dark");
	}, [theme]);

	useEffect(() => {
		if (theme !== "system") return;
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const onChange = () => {
			const r = resolveTheme("system");
			setResolved(r);
			document.documentElement.classList.toggle("dark", r === "dark");
		};
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, [theme]);

	const setTheme = useCallback((t: Theme) => {
		setThemeState(t);
		try {
			localStorage.setItem(STORAGE_KEY, t);
		} catch {
			/* ignore */
		}
	}, []);

	const value = useMemo(
		() => ({ theme, resolved, setTheme }),
		[theme, resolved, setTheme],
	);

	return (
		<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
	);
}

export function useTheme(): ThemeContextValue {
	const ctx = useContext(ThemeContext);
	if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
	return ctx;
}
